import { Typography, Button, SxProps, Theme } from '@mui/material';
import dayjs from 'dayjs';
import { CalendarDay } from '../../types';
import { useDatePickerContext } from '../../hooks/use-date-picker-context';

type Props = {
  calendarDay: CalendarDay;
  sx?: SxProps<Theme>;
};

export function DayCell({ calendarDay, sx }: Props) {
  const { value, onChange, disableDaysAfter, disableDaysBefore, disabledDays, shouldDisableDay } =
    useDatePickerContext();
  const isSelectedDay = calendarDay.date.isSame(value, 'day');

  const handleClick = () => {
    let newDate = calendarDay.date.clone();
    if (value) {
      const currentTime = dayjs(value);
      newDate = newDate
        .hour(currentTime.hour())
        .minute(currentTime.minute())
        .second(currentTime.second());
    }
    onChange(newDate.toDate());
  };

  const getColor = (isHover: boolean) => {
    if (isSelectedDay) return '#fff';
    if (isHover) return '#2055FF';
    if (!calendarDay.isCurrentMonth) return '#667085';
    return '#344054';
  };

  const isDisabled = (() => {
    if (disableDaysAfter && calendarDay.date.isAfter(disableDaysAfter, 'day')) return true;
    if (disableDaysBefore && calendarDay.date.isBefore(disableDaysBefore, 'day')) return true;
    if (disabledDays?.some((d) => calendarDay.date.isSame(d, 'day'))) return true;
    if (shouldDisableDay) return shouldDisableDay(calendarDay);
    return false;
  })();

  return (
    <Button
      onClick={handleClick}
      disabled={isDisabled}
      sx={{
        width: '40px !important',
        height: '40px !important',
        minWidth: '40px !important',
        minHeight: '40px !important',
        maxWidth: '40px !important',
        maxHeight: '40px !important',
        padding: '0 !important',
        margin: '0 !important',
        border: 'none !important',
        '&.MuiButton-root': {
          padding: '0 !important',
          minWidth: '40px !important',
        },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        background: isSelectedDay ? '#2055FF' : 'transparent',
        color: getColor(false) || '#344054',
        transition: 'background-color 0.2s ease, color 0.2s ease',
        '&:hover': {
          background: isSelectedDay ? '#2055FF' : '#F2F4F7',
          color: getColor(true) || '#2055FF',
        },

        ...sx,
      }}
    >
      <Typography
        sx={{
          color: 'currentColor',
          textAlign: 'center',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: isSelectedDay ? 500 : 400,
          lineHeight: '20px',
        }}
      >
        {calendarDay.date.format('D')}
      </Typography>
    </Button>
  );
}
